import * as React from "react";

import { useNavigation } from "@/providers";
import { DetailActions } from "@/shared/components/DetailActions";
import { FactList } from "@/shared/components/FactList";
import {
  FormCount,
  FormDivider,
  FormField,
  FormLinkButton,
  FormRow,
} from "@/shared/components/FormSection";
import { TriggerTargetIngestionNudge } from "@/shared/components/ingestionRules";
import { type TriggeringSubject } from "@/shared/components/invocationSpec/inputSourceCopy";
import { AgentLink } from "@/shared/components/links/AgentLink";
import { SlackChannelLink } from "@/shared/components/links/SlackChannelLink";
import { browserTimeZone } from "@/shared/datetime";
import { ingestionRuleIdOf } from "@/shared/domain/ingestionRules";
import {
  actionTakesTheFile,
  entityLabel as labelOfEntity,
  entityOfSubscription,
  eventLabel,
  isSchedule,
  isManaged,
  OncePer,
  oncePerChoices,
  readableCondition,
  readableCron,
  readableSchedule,
  runsOnPhrase,
  scheduleFromUtcCron,
  sourceEventTypes,
  TARGET_TYPE_LABELS,
  TriggerTargetType,
  variableRoots,
  type PlatformEventType,
  type TriggerSource,
  type TriggerTargetSpec,
  type TriggerRecord,
} from "@/shared/domain/triggers";
import {
  useDeleteTrigger,
  useToggleTrigger,
} from "@/shared/domain/triggers/hooks";
import { Pencil } from "@/shared/foundation/icons";
import {
  Banner,
  ConfirmDialog,
  FormSection,
  Switch,
} from "@/shared/foundation/primitives";
import { RobotoButton } from "@/shared/foundation/primitives/RobotoButton";

import { isEditableInForm } from "../TriggerForm/formScope";

import { InvocationSpecFacts } from "./InvocationSpecFacts";
import {
  ManagedByIngestionRuleNote,
  ManagingRuleSummary,
} from "./ManagedByIngestionRuleNote";
import { systemManagedExplanation } from "./managedTriggerExplanation";
import { SampleEventDialog } from "./SampleEventDialog";
import { TargetCard, TargetCardName } from "./TargetCard";
import { countClauses } from "./triggerConditionFilters";
import { TriggerFacts } from "./TriggerFacts";
import styles from "./TriggerOverview.module.css";
import { TriggerOwnerBadge } from "./TriggerOwnerBadge";
import { TriggerSentence } from "./TriggerSentence";

/**
 * The Details tab: the trigger in the same sections the create form
 * uses, read rather than edited, so a reader who has just built one finds
 * the same shape here. The sentence leads as the summary. Edit opens the form
 * on its own route; enable and delete sit in a row of their own because they
 * are lifecycle, not properties of the trigger.
 */
export function TriggerOverview({ record }: { record: TriggerRecord }) {
  const { goto } = useNavigation();

  const [errorText, setErrorText] = React.useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [sampleOpen, setSampleOpen] = React.useState(false);

  const {
    checked: enabled,
    pending: toggling,
    toggle,
  } = useToggleTrigger(record, (error) => setErrorText(error.message));
  const deleteTrigger = useDeleteTrigger();

  const schedule = isSchedule(record.fires_on) ? record.fires_on : null;
  const subscription = isSchedule(record.fires_on) ? null : record.fires_on;
  const clauseCount = countClauses(record.condition ?? null);
  const managed = isManaged(record);
  // A rule-managed trigger's note names the rule and links to it.
  const managingRuleId = ingestionRuleIdOf(record);
  const editable = isEditableInForm(record) && !managed;
  let editNoteId: string | undefined = undefined;
  if (managed) {
    editNoteId = "managed-trigger-note";
  } else if (!editable) {
    editNoteId = "edit-trigger-note";
  }

  return (
    <div className={styles.page}>
      {errorText !== null && <Banner variant="error">{errorText}</Banner>}

      <DetailActions
        start={
          <>
            <Switch
              checked={enabled}
              // A rule's trigger is turned on and off with the rule's auto-ingest.
              disabled={toggling || managingRuleId !== null}
              onCheckedChange={(checked) => {
                setErrorText(null);
                toggle(checked);
              }}
              label="Enabled"
              description={
                enabled
                  ? "Starts when its platform events match."
                  : "Nothing starts until it is enabled again."
              }
              aria-describedby={
                managingRuleId !== null ? "managed-trigger-note" : undefined
              }
            />
            <TriggerOwnerBadge trigger={record} />
          </>
        }
      >
        {managingRuleId !== null && (
          <ManagedByIngestionRuleNote
            ruleId={managingRuleId}
            id="managed-trigger-note"
            className={styles.editNote}
          />
        )}
        {managed && managingRuleId === null && (
          <p className={styles.editNote} id="managed-trigger-note">
            {systemManagedExplanation(record.managed_by)}
          </p>
        )}
        {!managed && !editable && (
          <p className={styles.editNote} id="edit-trigger-note">
            This trigger watches something the form cannot show yet, so it is
            edited through the SDK.
          </p>
        )}
        <RobotoButton
          variant="secondary"
          icon={<Pencil aria-hidden="true" />}
          disabled={!editable}
          aria-describedby={editNoteId}
          onClick={() => goto.editTrigger({ triggerName: record.name })}
          id="edit-trigger-button"
          data-testid="edit-trigger-button"
        >
          Edit
        </RobotoButton>
        <RobotoButton
          variant="destructive-secondary"
          onClick={() => setDeleteOpen(true)}
          disabled={managed}
          aria-describedby={managed ? editNoteId : undefined}
          id="delete-trigger-button"
          data-testid="delete-trigger-button"
        >
          Delete
        </RobotoButton>
      </DetailActions>
      {managingRuleId !== null && (
        <ManagingRuleSummary
          ruleId={managingRuleId}
          className={styles.ruleSummary}
        />
      )}

      <FormSection title="Summary">
        <div className={styles.sentenceFrame}>
          <TriggerSentence record={record} />
        </div>
      </FormSection>

      <FormSection
        title="Starts on"
        hint="What makes this trigger run."
        actions={
          subscription !== null && (
            <FormLinkButton
              onClick={() => setSampleOpen(true)}
              data-cy="view-sample-event"
            >
              View sample payload
            </FormLinkButton>
          )
        }
      >
        {schedule !== null ? (
          <FormField label="On a schedule">
            <p className={styles.value} data-cy="trigger-schedule">
              {readableSchedule(
                scheduleFromUtcCron(schedule.cron, browserTimeZone()),
              )}
            </p>
            <p className={styles.help}>
              Stored as <code className={styles.mono}>{schedule.cron}</code>,{" "}
              {readableCron(schedule.cron)}. Minutes missed during an outage are
              skipped, not caught up. If a target action is deleted, the trigger
              is disabled after the failed run.
            </p>
          </FormField>
        ) : (
          subscription !== null && (
            <>
              <FormRow>
                <FormField
                  label="Entity"
                  help="The kind of thing this trigger watches."
                >
                  <p className={styles.value}>
                    {entityLabel(subscription.events)}
                  </p>
                </FormField>
                <FormField label="Change" help="Any of these starts it.">
                  <div className={styles.chips}>
                    {subscription.events.map((eventType) => (
                      <span
                        key={eventType}
                        className={styles.chip}
                        title={eventType}
                      >
                        {eventLabel(eventType)}
                      </span>
                    ))}
                  </div>
                </FormField>
              </FormRow>

              <FormDivider />

              <FormField
                label="Start once per"
                help={grainMeaning(subscription.events, subscription.once_per)}
              >
                <p className={styles.value}>
                  {grainLabel(subscription.events, subscription.once_per)}
                </p>
              </FormField>
            </>
          )
        )}
      </FormSection>

      <FormSection
        title="Conditions"
        hint={
          schedule !== null
            ? "Not available for schedules."
            : "Only starts when the subject matches all of these."
        }
        actions={
          clauseCount > 0 ? (
            <FormCount>
              {clauseCount === 1 ? "1 condition" : `${clauseCount} conditions`}
            </FormCount>
          ) : undefined
        }
        className={schedule !== null ? styles.inert : undefined}
        dataCy="conditions"
      >
        <ConditionsBody record={record} />
      </FormSection>

      <FormSection
        title="Targets"
        hint="What runs when the trigger starts, in order."
      >
        <div className={styles.targets}>
          {record.targets.map((target, index) => (
            <TriggerTargetCard
              key={target.target_id}
              target={target}
              index={index}
              trigger={record}
            />
          ))}
          {record.targets.length === 0 && (
            <p className={styles.muted}>This trigger has no targets.</p>
          )}
        </div>
      </FormSection>

      <FormSection title="Details">
        <TriggerFacts record={record} />
      </FormSection>

      {/* Mounted only while open: the dialog fetches the samples on mount,
          and most visits never open it. */}
      {sampleOpen && (
        <SampleEventDialog
          open
          onClose={() => setSampleOpen(false)}
          events={sourceEventTypes(record.fires_on)}
        />
      )}

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete trigger?"
        description={`Delete the trigger '${record.name}'? This can't be undone.`}
        confirmLabel="Delete"
        destructive
        pending={deleteTrigger.isPending}
        onConfirm={() =>
          deleteTrigger.mutate(
            { name: record.name },
            {
              onError: (error) => {
                setDeleteOpen(false);
                setErrorText(error.message);
              },
              onSuccess: () => goto.triggers(),
            },
          )
        }
      />
    </div>
  );
}

/** The condition read as prose, or why there is none. */
function ConditionsBody({ record }: { record: TriggerRecord }) {
  if (isSchedule(record.fires_on)) {
    return <p className={styles.muted}>Schedules have nothing to filter on.</p>;
  }
  if (record.condition === null || record.condition === undefined) {
    return (
      <p className={styles.muted}>
        None &mdash; every matching change starts this trigger.
      </p>
    );
  }
  return (
    <>
      <p className={styles.prose}>{readableCondition(record.condition)}</p>
      {/* The prose is the readable form; the stored shape stays one click
          away for anyone reconciling it against the SDK or an API response. */}
      <details className={styles.raw}>
        <summary>Stored form</summary>
        <pre>{JSON.stringify(record.condition, null, 2)}</pre>
      </details>
    </>
  );
}

/**
 * One target, in the same card the editor uses: a mono "Target N" pill, its
 * type, the thing it acts on, then the configuration that decides what it
 * actually does.
 */
function TriggerTargetCard({
  target,
  index,
  trigger,
}: {
  target: TriggerTargetSpec;
  index: number;
  trigger: TriggerRecord;
}) {
  return (
    <TargetCard
      pill={`Target ${index + 1}`}
      type={TARGET_TYPE_LABELS[target.type]}
      name={<TargetName target={target} />}
      data-cy="target-card"
    >
      <TargetDetail target={target} firesOn={trigger.fires_on} />
      {target.type === TriggerTargetType.InvokeAction && (
        <TriggerTargetIngestionNudge trigger={trigger} target={target} />
      )}
    </TargetCard>
  );
}

/** "File", or "Several" for an SDK-authored subscription spanning entities. */
function entityLabel(events: readonly PlatformEventType[]): string {
  const entity = entityOfSubscription(events);
  return entity === null ? "Several" : labelOfEntity(entity);
}

/** The chosen grain's label, as the editor's radios show it. */
function grainLabel(
  events: readonly PlatformEventType[],
  oncePer: OncePer,
): string {
  return (
    oncePerChoices(events).find((choice) => choice.value === oncePer)?.label ??
    oncePer
  );
}

function grainMeaning(
  events: readonly PlatformEventType[],
  oncePer: OncePer,
): string {
  return (
    oncePerChoices(events).find((choice) => choice.value === oncePer)
      ?.description ?? ""
  );
}

function TargetName({ target }: { target: TriggerTargetSpec }) {
  switch (target.type) {
    case TriggerTargetType.InvokeAction:
      return <TargetCardName>{target.action.name}</TargetCardName>;
    case TriggerTargetType.StartAgent:
      return (
        <TargetCardName>
          <AgentLink agentId={target.agent_id} />
        </TargetCardName>
      );
    case TriggerTargetType.SendSlackMessage:
      return <SlackChannelLink channelId={target.channel_id} />;
  }
}

/**
 * What a firing is about, for wording an input on the dataset it carries, or
 * undefined when it carries none. Mirrors how the editor words it.
 */
function triggeringSubjectOf(
  firesOn: TriggerSource,
): TriggeringSubject | undefined {
  if (
    isSchedule(firesOn) ||
    !variableRoots(firesOn.events).includes("dataset")
  ) {
    return undefined;
  }
  const entity = entityOfSubscription(firesOn.events);
  return (entity === "file" || entity === "dataset") &&
    actionTakesTheFile(firesOn.events, firesOn.once_per)
    ? "file"
    : "dataset";
}

function TargetDetail({
  target,
  firesOn,
}: {
  target: TriggerTargetSpec;
  firesOn: TriggerSource;
}) {
  switch (target.type) {
    case TriggerTargetType.InvokeAction: {
      const triggeringSubject = triggeringSubjectOf(firesOn);
      return (
        <FactList>
          <FactList.Item label="Runs on">
            {runsOnPhrase(target, firesOn)}
          </FactList.Item>
          <InvocationSpecFacts
            spec={target}
            triggeringSubject={triggeringSubject}
            defaultOutput={
              triggeringSubject === undefined
                ? "None"
                : "The dataset that changed"
            }
          />
        </FactList>
      );
    }
    case TriggerTargetType.StartAgent:
      return (
        <FactList>
          <FactList.Item label="Thread visibility">
            {target.visibility}
          </FactList.Item>
          {Object.keys(target.values).length > 0 && (
            <FactList.Item label="Values" mono>
              {Object.entries(target.values)
                .map(([name, value]) => `${name} = ${value}`)
                .join(", ")}
            </FactList.Item>
          )}
        </FactList>
      );
    case TriggerTargetType.SendSlackMessage:
      return (
        <FactList>
          <FactList.Item label="Message" valueClassName={styles.message}>
            {target.text}
          </FactList.Item>
        </FactList>
      );
  }
}
